/* =====================================================
   Full Banner Slider — full-banner-slider.js
   Improved Drag (Pixel-based, smooth, no jitter)
   FIX: Multiple instances (Shopify sections)
   FIX: Links inside banner are now clickable
   ===================================================== */

class FullBannerSlider {
  constructor(container) {
    // Drag state
    this._isDragging = false;
    this._dragStartX = 0;
    this._hasDragged = false;
    this._dragThreshold = 8;   // px
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.track = container.querySelector(`#SliderTrack-${this.sectionId}`);
    this.slides = container.querySelectorAll('.full-banner-slider__slide');
    this.dots = container.querySelectorAll('.full-banner-slider__dot');
    this.arrows = container.querySelectorAll('.full-banner-slider__arrow');
    this.wrapper = container.querySelector('.full-banner-slider__wrapper');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    this.autoplayTimer = null;

    // Settings
    this.autoplay = container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(container.dataset.autoplaySpeed, 10) || 5000;
    this.pauseOnHover = container.dataset.pauseOnHover === 'true';
    this.transition = container.dataset.transition || 'slide';

    // Drag state
    this._isDragging = false;
    this._dragStartX = 0;
    this._hasDragged = false;

    // Bound handlers
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);

    this.init();
  }

  init() {
    // RESET visual (evita bugs ao recriar seção)
    this.track.style.transform = 'translateX(0px)';
    this.track.style.transition = 'none';

    requestAnimationFrame(() => {
      this.track.style.transition = '';
    });

    this.applyTransitionMode();
    this.bindArrows();
    this.bindDots();
    this.bindSwipe();
    this.bindKeyboard();

    if (this.autoplay && this.totalSlides > 1) {
      this.startAutoplay();

      if (this.pauseOnHover) {
        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());
        this.container.addEventListener('focusin', () => this.stopAutoplay());
        this.container.addEventListener('focusout', () => this.startAutoplay());
      }
    }
  }

  applyTransitionMode() {
    if (this.transition === 'fade') {
      this.wrapper.classList.add('full-banner-slider--fade');
    }
  }

  goTo(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    if (this.totalSlides <= 1) return;

    this.isAnimating = true;

    const prevIndex = this.currentIndex;
    this.currentIndex = (index + this.totalSlides) % this.totalSlides;

    if (this.transition === 'fade') {
      this.fadeTransition(prevIndex, this.currentIndex);
    } else {
      this.slideTransition(this.currentIndex);
    }

    this.updateDots(this.currentIndex);

    setTimeout(() => {
      this.isAnimating = false;
    }, 500);
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  slideTransition(index) {
    const slideWidth = this.wrapper.offsetWidth;
    this.track.style.transition = 'transform 0.5s ease';
    this.track.style.transform = `translateX(-${index * slideWidth}px)`;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });
  }

  fadeTransition(prevIndex, nextIndex) {
    this.slides[prevIndex].classList.remove('is-active');
    this.slides[nextIndex].classList.add('is-active');
  }

  updateDots(index) {
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  bindArrows() {
    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        arrow.dataset.direction === 'prev' ? this.prev() : this.next();

        if (this.autoplay) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      });
    });
  }

  bindDots() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.goTo(index);

        if (this.autoplay) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      });
    });
  }

  bindKeyboard() {
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

_onMouseMove(e) {
  if (!this._isDragging) return;

  const diff = e.clientX - this._dragStartX;

  // Activate drag only after threshold
  if (!this._hasDragged) {
    if (Math.abs(diff) < this._dragThreshold) return;

    this._hasDragged = true;
    this.wrapper.classList.add('is-dragging');
    this.track.style.transition = 'none';
  }

  const slideWidth = this.wrapper.offsetWidth;
  const base = -this.currentIndex * slideWidth;

  this.track.style.transform = `translateX(${base + diff}px)`;
}

bindSwipe() {
  if (this.transition === 'fade') return;

  this.wrapper.addEventListener('dragstart', (e) => {
    e.preventDefault();
  });

  this.wrapper.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;

    this._isDragging = true;
    this._dragStartX = e.clientX;
    this._hasDragged = false;

    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
  });

  // Block clicks ONLY after a real drag
  this.wrapper.addEventListener(
    'click',
    (e) => {
      if (!this._hasDragged) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      this._hasDragged = false;
    },
    true
  );
}

_onMouseUp(e) {
  if (!this._isDragging) return;

  const diff = e.clientX - this._dragStartX;
  const slideWidth = this.wrapper.offsetWidth;

  this._isDragging = false;

  window.removeEventListener('mousemove', this._onMouseMove);
  window.removeEventListener('mouseup', this._onMouseUp);

  // It was just a click
  if (!this._hasDragged) {
    return;
  }

  this.wrapper.classList.remove('is-dragging');

  this.track.style.transition = 'transform 0.5s ease';

  const threshold = slideWidth * 0.15;

  if (Math.abs(diff) > threshold) {
    if (diff < 0) {
      this.next();
    } else {
      this.prev();
    }
  } else {
    this.track.style.transform = `translateX(-${this.currentIndex * slideWidth}px)`;
  }

  if (this.autoplay) {
    this.stopAutoplay();
    this.startAutoplay();
  }

  setTimeout(() => {
    this._hasDragged = false;
  }, 50);
}
}

/* =====================================================
   INIT (FIXED)
   ===================================================== */

function initFullBannerSliders() {
  document.querySelectorAll('.full-banner-slider').forEach((el) => {
    if (el.sliderInstance) {
      el.sliderInstance.stopAutoplay();
    }

    el.sliderInstance = new FullBannerSlider(el);
  });
}

document.addEventListener('DOMContentLoaded', initFullBannerSliders);

// Shopify Editor FIX
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.full-banner-slider');

  if (section) {
    if (section.sliderInstance) {
      section.sliderInstance.stopAutoplay();
    }

    section.sliderInstance = new FullBannerSlider(section);
  }
});

document.addEventListener('shopify:section:unload', (e) => {
  const section = e.target.querySelector('.full-banner-slider');

  if (section?.sliderInstance) {
    section.sliderInstance.stopAutoplay();
    delete section.sliderInstance;
  }
});

document.addEventListener('shopify:block:select', (e) => {
  const section = e.target.closest('.full-banner-slider');

  if (section?.sliderInstance) {
    const index = parseInt(e.target.dataset.index, 10);

    if (!isNaN(index)) {
      section.sliderInstance.stopAutoplay();
      section.sliderInstance.goTo(index);
    }
  }
});
